import React from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Grid, Typography } from '@mui/material';
import SparkCarousel from 'sharedComponents/UI/SparkCarousel';
import { SparkDialog, SparkUISlider } from 'sharedComponents/UI';
import { useDialog } from 'hooks/common';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import { SparkImageLoader } from 'sharedComponents/UI';

type PreviewTabTypeProps = {
  joinedSpark: JoinedSparks | undefined;
};

const fileType = {
  IMAGE: 'image',
  VIDEO: 'video',
} as const;

function PreviewTab({ joinedSpark }: PreviewTabTypeProps) {
  const [imageIndex, setImageIndex] = React.useState(0);
  const {
    open: isImageDialogOpen,
    openDialog: openImageDialog,
    closeDialog: closeImageDialog,
  } = useDialog();

  const { videoURL } = joinedSpark?.attributes || {};

  const carouselImages = React.useMemo(
    () =>
      joinedSpark?.sparkImages?.map((image) => ({
        url: image.attributes.imageURL,
        alt: image.attributes.imageURL,
        type: fileType.IMAGE,
      })),
    [joinedSpark?.sparkImages]
  );

  const getEmbedUrl = (url: string) => {
    if (url.includes('youtu.be')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }

    if (url.includes('loom.com/share')) {
      const videoId = url.split('/').pop();
      return `https://www.loom.com/embed/${videoId}`;
    }

    return url;
  };

  const getThumbnailUrl = (url: string) => {
    if (url.includes('youtu.be')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }

    return url;
  };

  const sliderItems = [
    ...(videoURL
      ? [{ type: fileType.VIDEO, url: getEmbedUrl(videoURL), alt: videoURL }]
      : []),
    ...(carouselImages ? carouselImages : []),
  ];

  return (
    <Grid container alignItems='center' sx={{ paddingTop: '8px' }}>
      {sliderItems.length > 0 && (
        <SparkCarousel
          onLastElementView={() => {}}
          parentContainerStyles={{
            justifyContent: 'center',
            display: 'flex',
          }}
          renderArrowButtons={sliderItems.length > 1}
        >
          {sliderItems.map((file, index) => {
            if (file.type === 'video') {
              return (
                <Box
                  key={`video-${index}`}
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  width='100%'
                  height='100%'
                  bgcolor={(theme) => theme.palette.primary.light}
                  sx={{
                    aspectRatio: 1,
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  borderRadius='10px'
                  onClick={() => {
                    setImageIndex(index);
                    openImageDialog();
                  }}
                >
                  <SparkImageLoader
                    src={getThumbnailUrl(videoURL as string) || ''}
                    alt=''
                    width='90%'
                    height='90%'
                  />
                  <PlayArrowIcon
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: 'white',
                      height: '40px',
                      width: '40px',
                    }}
                  />
                </Box>
              );
            }

            if (file.type === 'image') {
              return (
                <Box
                  key={`image-${index}`}
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  width='100%'
                  height='100%'
                  bgcolor={(theme) => theme.palette.primary.light}
                  sx={{ aspectRatio: 1, cursor: 'pointer' }}
                  borderRadius='10px'
                  onClick={() => {
                    setImageIndex(index);
                    openImageDialog();
                  }}
                >
                  <SparkImageLoader
                    src={file.url || ''}
                    alt=''
                    width='90%'
                    height='90%'
                  />
                </Box>
              );
            }

            return null;
          })}
        </SparkCarousel>
      )}

      {!!joinedSpark?.attributes?.overview && (
        <Grid item xs={12} borderRadius='10px' padding={2}>
          <Typography
            sx={{
              fontSize: '16px',
              fontStyle: 'normal',
              borderRadius: '6px',
              whiteSpace: 'pre-line',
            }}
          >
            {joinedSpark?.attributes?.overview || '- -'}
          </Typography>
        </Grid>
      )}

      {isImageDialogOpen && (
        <SparkDialog
          open={isImageDialogOpen}
          onClose={closeImageDialog}
          title=''
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
          hideTitle={true}
          fullScreen={true}
        >
          <SparkUISlider
            sliderItems={sliderItems || []}
            initialIndex={imageIndex}
          />
        </SparkDialog>
      )}
    </Grid>
  );
}

export default PreviewTab;
